/**
 * LARCIN — Bundle Builder v5 (assets/bundle-builder.js)
 *
 * PRICING MODEL (v5 — replaces the v3/v4 marginal fixed-price curve)
 * -----------------------------------------------------------------
 * A percentage ladder driven entirely by the section's Milestone blocks:
 *
 *   pct(n) = the percentage of the highest milestone whose bottle count
 *            has been reached; 0 below the first discounting milestone.
 *   saving = round(subtotal × pct / 100)
 *   total  = subtotal − saving
 *
 * With the shipped ladder (2 free shipping, 3→15, 4→17, 5→20, 6→22, 7→25)
 * and a $50 bottle this reproduces exactly:
 *   2 → $100.00 (free shipping, no discount)
 *   3 → $127.50  save $22.50
 *   4 → $166.00  save $34.00
 *   5 → $200.00  save $50.00
 *   6 → $234.00  save $66.00
 *   7 → $262.50  save $87.50
 * Above the last milestone the top percentage carries on (8 → $300.00).
 *
 * Because the discount is a percentage of the ACTUAL selected variant
 * prices rather than a fixed dollar amount, a sale-priced bottle discounts
 * correctly with no special casing — which is also precisely how a Shopify
 * percentage automatic discount behaves.
 *
 * >>> ENFORCEMENT IS NOT DONE HERE. Shopify must carry quantity-tiered
 * >>> PERCENTAGE automatic discounts matching this ladder. This script only
 * >>> ever adds real variant IDs, never a product default, so it cannot be
 * >>> manipulated client-side into a price.
 *
 * v6 changes:
 *   - Direct checkout REVERTED. The primary action is once again "add
 *     bundle to cart, stay on the page" exactly as it behaved in v4, with
 *     no milestone gate and no redirect.
 *   - Milestone slots are larger and their reward callout now sits BELOW
 *     the box rather than banded across it, so a thumbnail is never
 *     covered.
 *   - The v5 sticky top tray is REMOVED. The v4 bottom bar is back, for
 *     both mobile and desktop. Anchoring anything beneath this theme's
 *     header proved unreliable: it is configured `scroll-up` sticky, so it
 *     is only `position: sticky` some of the time and there is no stable
 *     edge to hang from. The bottom of the viewport has no such problem.
 *     The bar now appears once the selector scrolls out of view rather
 *     than on first selection.
 *
 * Also from v5: sold-out cards render but can never be selected, and
 * desktop cards cross-fade to a second image hydrated only on pointer
 * devices.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

class BundleBuilder extends HTMLElement {
  /** @type {Map<string, {qty:number, productId:string, title:string, inspired:string, image:string, priceCents:number, cap:number}>} */
  #selections = new Map();
  #filters = { q: '', gender: 'all', scents: new Set(), sort: 'featured' };
  #config = null;
  #fmt = null;
  #loading = false;
  #allLoaded = false;
  #page = 1;
  #totalPages = 1;
  #observer = null;
  #topObserver = null;
  #searchTimer = null;
  #toastTimer = null;
  #submitting = false;
  #reachedTiers = new Set();
  #lastUpsellKey = '';
  #scentChipsBuilt = new Set();
  #slotEls = [];
  #lastCount = 0;
  #pastSelector = false;

  connectedCallback() {
    try {
      this.#config = JSON.parse(this.querySelector('[data-lbb-config]')?.textContent || '{}');
    } catch (e) {
      console.error('[bundle-builder] Bad config JSON', e);
      this.#config = { tiers: [], text: {}, unitPriceCents: 5000, maxBottles: 10 };
    }
    this.#config.tiers = (this.#config.tiers || [])
      .filter((t) => Number(t.qty) > 0)
      .map((t) => ({ ...t, qty: Number(t.qty), pct: Number(t.pct) || 0 }))
      .sort((a, b) => a.qty - b.qty);
    this.#fmt = new Intl.NumberFormat('en-AU', { style: 'currency', currency: this.#config.currency || 'AUD' });

    this.#page = Number(this.dataset.page || 1);
    this.#totalPages = Number(this.dataset.totalPages || 1);
    this.#allLoaded = this.#page >= this.#totalPages;

    this.#buildSlots();
    this.#bindEvents();
    this.#restoreStateFromURL();
    this.#indexCards(this.#allCards());
    this.#buildScentChips();
    this.#applyFilters();
    this.#setupInfiniteScroll();
    this.#setupStickyBar();
    this.#hydrateHoverImages();
    this.#refreshUI();

    this.#track('lbb_view', { collection: this.dataset.collectionUrl });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#topObserver?.disconnect();
    clearTimeout(this.#searchTimer);
    clearTimeout(this.#toastTimer);
  }

  /* ================= pricing engine ================= */

  get #unit() { return this.#config.unitPriceCents || 5000; }
  get #tiers() { return this.#config.tiers; }
  get #firstTier() { return this.#tiers[0] || null; }
  get #lastTier() { return this.#tiers[this.#tiers.length - 1] || null; }

  /** Highest milestone fully reached at n, or null. */
  #currentTier(n) {
    let hit = null;
    for (const t of this.#tiers) if (n >= t.qty) hit = t;
    return hit;
  }

  /** First milestone not yet reached at n, or null. */
  #nextTier(n) { return this.#tiers.find((t) => n < t.qty) || null; }

  /**
   * Discount percentage at n. Takes the maximum across reached milestones
   * rather than simply the last one, so an out-of-order ladder entered in
   * the theme editor can never quietly lower a customer's discount.
   */
  #pctFor(n) {
    let pct = 0;
    for (const t of this.#tiers) if (n >= t.qty && t.pct > pct) pct = t.pct;
    return pct;
  }

  /** Sum of the ACTUAL selected variant prices (sale prices included). */
  #subtotal() {
    let cents = 0;
    for (const s of this.#selections.values()) cents += s.qty * (s.priceCents || this.#unit);
    return cents;
  }

  #savingCents() { return Math.round((this.#subtotal() * this.#pctFor(this.#count())) / 100); }

  /** What checkout will charge, given matching percentage automatic discounts. */
  #bundleTotal() { return Math.max(0, this.#subtotal() - this.#savingCents()); }

  /** Reward wording for a milestone: explicit label, else "15% OFF". */
  #reward(tier) {
    if (!tier) return '';
    const label = (tier.reward || '').trim();
    if (label) return label.toUpperCase();
    return tier.pct > 0 ? `${tier.pct}% OFF` : '';
  }

  /* ================= helpers ================= */

  #q(sel) { return this.querySelector(sel); }
  #qa(sel) { return Array.from(this.querySelectorAll(sel)); }
  #allCards() { return this.#qa('[data-lbb-card]'); }
  #money(cents) { return this.#fmt.format(cents / 100); }
  #moneyShort(cents) { return this.#money(cents).replace(/\.00$/, ''); }
  #count() { let n = 0; for (const s of this.#selections.values()) n += s.qty; return n; }
  #bottles(k) { return k === 1 ? '1 more bottle' : `${k} more bottles`; }

  #esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  #normalize(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’'`´\u2018\u2019.,\-–—_()|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #track(event, data = {}) {
    const payload = { event, ...data };
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
      document.dispatchEvent(new CustomEvent(event, { detail: payload, bubbles: true }));
    } catch (e) { /* analytics must never break the builder */ }
  }

  #toast(msg, isError = false) {
    const toast = this.#q('[data-lbb-toast]');
    const text = this.#q('[data-lbb-toast-text]');
    if (!toast || !text) return;
    text.textContent = msg;
    toast.classList.toggle('lbb-toast--error', isError);
    toast.classList.add('is-on');
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => toast.classList.remove('is-on'), 3600);
  }

  /* ================= state <-> URL ================= */

  #restoreStateFromURL() {
    const p = new URLSearchParams(location.search);
    this.#filters.q = p.get('q') || '';
    if (this.#filters.q) { const input = this.#q('[data-lbb-search]'); if (input) input.value = this.#filters.q; }
    const g = p.get('g'); if (['all', 'men', 'women', 'unisex'].includes(g)) this.#filters.gender = g;
    const sf = p.get('sf'); if (sf) this.#filters.scents = new Set(sf.split(',').filter(Boolean));
    const sort = p.get('sort'); if (sort) this.#filters.sort = sort;
    const sortSel = this.#q('[data-lbb-sort]'); if (sortSel) sortSel.value = this.#filters.sort;
    this.#qa('[data-lbb-gender]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.lbbGender === this.#filters.gender)));

    window.addEventListener('popstate', () => {
      const q = new URLSearchParams(location.search).get('q') || '';
      this.#filters.q = q;
      const input = this.#q('[data-lbb-search]'); if (input) input.value = q;
      this.#applyFilters();
    });
  }

  #pushStateToURL() {
    const p = new URLSearchParams(location.search);
    const setOrDel = (k, v) => { if (v) p.set(k, v); else p.delete(k); };
    setOrDel('q', this.#filters.q);
    setOrDel('g', this.#filters.gender === 'all' ? '' : this.#filters.gender);
    setOrDel('sf', Array.from(this.#filters.scents).join(','));
    setOrDel('sort', this.#filters.sort === 'featured' ? '' : this.#filters.sort);
    p.delete('page'); p.delete('section_id');
    const qs = p.toString();
    history.replaceState(history.state, '', `${location.pathname}${qs ? '?' + qs : ''}`);
  }

  /* ================= events ================= */

  #bindEvents() {
    const search = this.#q('[data-lbb-search]');
    search?.addEventListener('input', () => {
      clearTimeout(this.#searchTimer);
      this.#searchTimer = setTimeout(() => {
        this.#filters.q = search.value;
        this.#ensureAllLoaded();
        this.#applyFilters();
        this.#pushStateToURL();
        if (this.#filters.q.trim()) this.#track('lbb_search', { query: this.#filters.q.trim() });
      }, 200);
    });

    this.#qa('[data-lbb-gender]').forEach((b) =>
      b.addEventListener('click', () => {
        this.#filters.gender = b.dataset.lbbGender;
        this.#qa('[data-lbb-gender]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        this.#ensureAllLoaded();
        this.#applyFilters();
        this.#pushStateToURL();
        this.#track('lbb_filter', { filter: 'gender', value: this.#filters.gender });
      })
    );

    this.#q('[data-lbb-scents]')?.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-lbb-scent]');
      if (!chip) return;
      const key = chip.dataset.lbbScent;
      if (this.#filters.scents.has(key)) this.#filters.scents.delete(key);
      else this.#filters.scents.add(key);
      chip.setAttribute('aria-pressed', String(this.#filters.scents.has(key)));
      this.#ensureAllLoaded();
      this.#applyFilters();
      this.#pushStateToURL();
      this.#track('lbb_filter', { filter: 'scent', value: key, active: this.#filters.scents.has(key) });
    });

    this.#q('[data-lbb-sort]')?.addEventListener('change', (e) => {
      this.#filters.sort = e.target.value;
      this.#ensureAllLoaded();
      this.#applySort();
      this.#pushStateToURL();
      this.#track('lbb_filter', { filter: 'sort', value: this.#filters.sort });
    });

    // Card interactions (delegated — survives infinite-scroll appends).
    // Sold-out cards are refused here as well as being disabled in markup,
    // so a click, an Enter on the button, or a synthetic event all fail.
    this.#q('[data-lbb-grid]')?.addEventListener('click', (e) => {
      const card = e.target.closest('[data-lbb-card]');
      if (!card) return;
      if (this.#isSoldOut(card)) {
        if (e.target.closest('[data-lbb-add]') || !e.target.closest('a')) {
          this.#toast(this.#config.text.soldOutPick || 'That fragrance is sold out.', true);
        }
        return;
      }
      if (e.target.closest('[data-lbb-dec]')) { this.#changeQty(card, -1); return; }
      if (e.target.closest('[data-lbb-inc]') || e.target.closest('[data-lbb-add]')) { this.#changeQty(card, +1); return; }
      if (!e.target.closest('[data-lbb-qtyctrl]')) this.#changeQty(card, +1);
    });

    // Slot remove controls (delegated — slots are updated in place).
    this.#q('[data-lbb-slots]')?.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-lbb-slot-remove]');
      if (!remove) return;
      e.preventDefault();
      this.#changeQtyByKey(remove.dataset.lbbSlotRemove, -1);
    });

    this.#q('[data-lbb-slots-prev]')?.addEventListener('click', () => this.#nudgeSlots(-1));
    this.#q('[data-lbb-slots-next]')?.addEventListener('click', () => this.#nudgeSlots(+1));
    this.#q('[data-lbb-slots]')?.addEventListener('scroll', () => this.#updateSlotNav(), { passive: true });

    this.#qa('[data-lbb-atc]').forEach((b) => b.addEventListener('click', () => this.#addBundleToCart()));
    this.#qa('[data-lbb-open-drawer]').forEach((b) => b.addEventListener('click', () => this.#openDrawer()));
    this.#q('[data-lbb-close-drawer]')?.addEventListener('click', () => this.#q('[data-lbb-drawer]')?.close());
    this.#q('[data-lbb-drawer]')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) e.currentTarget.close();
    });
    this.#q('[data-lbb-drawer-list]')?.addEventListener('click', (e) => {
      const line = e.target.closest('[data-lbb-line]');
      if (!line) return;
      const key = line.dataset.lbbLine;
      if (e.target.closest('[data-lbb-line-inc]')) this.#changeQtyByKey(key, +1);
      if (e.target.closest('[data-lbb-line-dec]')) this.#changeQtyByKey(key, -1);
    });

    this.#q('[data-lbb-loadmore]')?.addEventListener('click', () => {
      this.removeAttribute('data-load-failed');
      this.#loadNextPage();
    });
  }

  /* ================= selection ================= */

  #isSoldOut(card) { return card?.dataset.soldout === 'true'; }

  #changeQty(card, delta) {
    const key = card.dataset.v50Id;
    if (!key) return;
    // Hard gate: a sold-out fragrance can never enter the selection, no
    // matter which interaction path got here.
    if (delta > 0 && this.#isSoldOut(card)) {
      this.#toast(this.#config.text.soldOutPick || 'That fragrance is sold out.', true);
      return;
    }

    if (delta > 0) {
      const count = this.#count();
      if (count >= (this.#config.maxBottles || 10)) {
        this.#toast(this.#config.text.maxReached || 'Maximum bundle size reached.', true);
        return;
      }
      const existing = this.#selections.get(key);
      if (existing && !this.#config.allowDuplicates) {
        this.#toast('Duplicates are turned off for this bundle.', true);
        return;
      }
      const cap = this.#capFor(card);
      if (existing && existing.qty >= cap) {
        this.#toast(`Only ${cap} of ${card.dataset.title} left in stock.`, true);
        return;
      }
      if (existing) existing.qty += 1;
      else {
        this.#selections.set(key, {
          qty: 1,
          productId: card.dataset.productId,
          title: card.dataset.title,
          inspired: card.dataset.inspired,
          image: card.querySelector('.lbb-card__img')?.currentSrc || card.querySelector('.lbb-card__img')?.src || '',
          priceCents: Number(card.dataset.v50Price) || this.#unit,
          cap,
        });
      }
      this.#track('lbb_add', { handle: card.dataset.handle, variantId: key, count: this.#count() });
    } else {
      const existing = this.#selections.get(key);
      if (!existing) return;
      existing.qty -= 1;
      if (existing.qty <= 0) this.#selections.delete(key);
      this.#track('lbb_remove', { handle: card.dataset.handle, variantId: key, count: this.#count() });
    }

    this.#refreshUI();
  }

  /** Per-item quantity ceiling: live inventory for tracked variants, else a soft cap. */
  #capFor(card) {
    if (card.dataset.v50Managed === 'true') {
      return Math.max(0, Number(card.dataset.v50Inv) || 0);
    }
    return this.#config.maxBottles || 10;
  }

  #changeQtyByKey(key, delta) {
    const card = this.#allCards().find((c) => c.dataset.v50Id === key);
    if (card) {
      this.#changeQty(card, delta);
    } else {
      const sel = this.#selections.get(key);
      if (!sel) return;
      if (delta > 0) {
        if (this.#count() >= (this.#config.maxBottles || 10)) { this.#toast(this.#config.text.maxReached || 'Maximum reached.', true); return; }
        if (sel.cap != null && sel.qty >= sel.cap) { this.#toast(`Only ${sel.cap} of ${sel.title} left in stock.`, true); return; }
        sel.qty += 1;
      } else {
        sel.qty -= 1;
        if (sel.qty <= 0) this.#selections.delete(key);
      }
      this.#refreshUI();
    }
  }

  /* ================= hover images ================= */

  /**
   * Second images ship without a src. They are only given one on devices
   * with a real pointer, during idle time, so touch users never download
   * an image they can't reveal. Because the element is already in the DOM
   * and absolutely positioned, revealing it shifts nothing.
   */
  #hydrateHoverImages() {
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;
    const run = () => {
      this.#qa('img[data-lbb-src2]').forEach((img) => {
        const src = img.dataset.lbbSrc2;
        if (!src) return;
        img.src = src;
        delete img.dataset.lbbSrc2;
      });
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2500 });
    else setTimeout(run, 1200);
  }

  /* ================= slot selector ================= */

  /**
   * Slots are built once and updated in place, so thumbnails never reload
   * and keyboard focus survives a change. Every slot carries the same
   * elements — badge rail, box, name rail — so all slots are exactly the
   * same size whether or not they are a milestone or hold a product.
   */
  #buildSlots() {
    const strip = this.#q('[data-lbb-slots]');
    if (!strip) return;
    const max = this.#config.maxBottles || 10;
    const frag = document.createDocumentFragment();
    this.#slotEls = [];

    for (let i = 0; i < max; i += 1) {
      const tier = this.#tiers.find((t) => t.qty === i + 1) || null;

      const li = document.createElement('li');
      li.className = 'lbb-slot-item';
      li.dataset.lbbSlot = String(i);

      // Badge rail — always present so every slot is the same height.
      const marker = document.createElement('span');
      marker.className = 'lbb-slot__marker';
      const badge = (tier?.badge || '').trim();
      if (badge) marker.textContent = badge;
      else marker.classList.add('is-empty');

      const box = document.createElement('div');
      box.className = 'lbb-slot';
      if (tier) box.classList.add('is-milestone');
      box.setAttribute('aria-hidden', 'true');

      const plus = document.createElement('span');
      plus.className = 'lbb-slot__plus';
      plus.setAttribute('aria-hidden', 'true');
      plus.textContent = '+';

      const img = document.createElement('img');
      img.className = 'lbb-slot__img';
      img.alt = '';
      img.loading = 'lazy';
      img.hidden = true;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lbb-slot__x';
      remove.innerHTML = '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
      remove.hidden = true;

      // The slot number lives inside the box on its own chip so it stays
      // legible over a thumbnail without a band across the artwork.
      const num = document.createElement('span');
      num.className = 'lbb-slot__n';
      num.textContent = String(i + 1);

      box.append(plus, img, remove, num);

      // Reward callout — BELOW the box, never across it, so a selected
      // bottle's thumbnail is never covered.
      const reward = document.createElement('span');
      reward.className = 'lbb-slot__reward';
      const rewardText = this.#reward(tier);
      if (rewardText) reward.textContent = rewardText;
      else reward.classList.add('is-empty');

      // Name rail — always present, clamped to two lines.
      const name = document.createElement('span');
      name.className = 'lbb-slot__name';

      li.append(marker, box, reward, name);
      frag.appendChild(li);
      this.#slotEls.push({ li, box, img, remove, reward, name, tier });
    }

    strip.replaceChildren(frag);
  }

  /** Flat list of selected variant keys, one entry per bottle, in pick order. */
  #slotKeys() {
    const keys = [];
    for (const [key, sel] of this.#selections) for (let i = 0; i < sel.qty; i += 1) keys.push(key);
    return keys;
  }

  #renderSlots(n) {
    if (!this.#slotEls.length) return;
    const keys = this.#slotKeys();

    this.#slotEls.forEach((slot, i) => {
      const key = keys[i];
      const sel = key ? this.#selections.get(key) : null;
      const { li, box, img, remove, reward, name, tier } = slot;

      li.classList.toggle('is-filled', Boolean(sel));
      box.classList.toggle('is-filled', Boolean(sel));
      box.classList.toggle('is-next', !sel && i === n);
      if (tier) {
        const unlocked = n >= tier.qty;
        box.classList.toggle('is-unlocked', unlocked);
        reward.classList.toggle('is-unlocked', unlocked);
        // The single milestone the customer is currently sitting on.
        const current = this.#currentTier(n) === tier;
        box.classList.toggle('is-current', current);
        reward.classList.toggle('is-current', current);
      }

      if (sel) {
        if (img.getAttribute('src') !== sel.image) img.setAttribute('src', sel.image);
        img.hidden = false;
        img.alt = sel.title;
        remove.hidden = false;
        remove.dataset.lbbSlotRemove = key;
        remove.setAttribute('aria-label', `Remove ${sel.title} from your bundle`);
        name.textContent = sel.title;
      } else {
        img.hidden = true;
        img.removeAttribute('src');
        remove.hidden = true;
        delete remove.dataset.lbbSlotRemove;
        name.textContent = '';
      }
    });

    // Keep the slot being filled next in view, without scrolling the page.
    if (n !== this.#lastCount) {
      const strip = this.#q('[data-lbb-slots]');
      const target = strip?.querySelector('.lbb-slot.is-next')?.closest('.lbb-slot-item') || strip?.lastElementChild;
      if (strip && target && strip.scrollWidth > strip.clientWidth) {
        const left = target.offsetLeft - (strip.clientWidth - target.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
      this.#lastCount = n;
    }
    this.#updateSlotNav();
  }

  #nudgeSlots(dir) {
    const strip = this.#q('[data-lbb-slots]');
    if (!strip) return;
    strip.scrollBy({ left: dir * Math.max(160, strip.clientWidth * 0.7), behavior: 'smooth' });
  }

  #updateSlotNav() {
    const strip = this.#q('[data-lbb-slots]');
    const prev = this.#q('[data-lbb-slots-prev]');
    const next = this.#q('[data-lbb-slots-next]');
    if (!strip) return;
    const overflowing = strip.scrollWidth - strip.clientWidth > 4;
    if (prev) prev.hidden = !overflowing || strip.scrollLeft <= 4;
    if (next) next.hidden = !overflowing || strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 4;
  }

  /* ================= sticky bottom bar ================= */

  /**
   * v7: back to a bottom bar. Nothing needs to be measured — the bottom of
   * the viewport is always where it says it is, unlike this theme's
   * `scroll-up` header, which is only sticky part of the time.
   *
   * Visibility is driven by the selector leaving the viewport, so the bar
   * never doubles up with the panel the customer is already looking at.
   */
  #setupStickyBar() {
    const bar = this.#q('[data-lbb-sticky]');
    if (!bar) return;

    const selector = this.#q('[data-lbb-selector]');
    if (!selector || !('IntersectionObserver' in window)) {
      this.#pastSelector = true;
      this.#updateStickyBar();
      return;
    }
    // Fires when the selector's bottom edge passes above the viewport.
    this.#topObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#pastSelector = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
        this.#updateStickyBar();
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.#topObserver.observe(selector);
  }

  #updateStickyBar() {
    const bar = this.#q('[data-lbb-sticky]');
    if (!bar) return;
    const always = this.dataset.stickyAlways === 'true';
    const show = this.#pastSelector && (always || this.#count() > 0);
    bar.classList.toggle('is-visible', show);
    this.toggleAttribute('data-bar-visible', show);
  }

  /* ================= scent chips ================= */

  #buildScentChips() {
    const wrap = this.#q('[data-lbb-scents]');
    if (!wrap) return;
    const keywords = (this.#config.scentKeywords || []).map((k) => k.trim()).filter(Boolean);
    const familiesPresent = this.#allCards().map((c) => this.#normalize(c.dataset.family));
    for (const kw of keywords) {
      const key = this.#normalize(kw);
      if (this.#scentChipsBuilt.has(key)) continue;
      if (!familiesPresent.some((f) => f.includes(key))) continue;
      this.#scentChipsBuilt.add(key);
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'lbb-chip';
      chip.dataset.lbbScent = key;
      chip.textContent = kw;
      chip.setAttribute('aria-pressed', String(this.#filters.scents.has(key)));
      wrap.appendChild(chip);
    }
  }

  /* ================= filtering / sorting / search ================= */

  #indexCards(cards) {
    for (const card of cards) {
      if (card.dataset.lbbIndexed) continue;
      card.dataset.search = this.#normalize(
        [card.dataset.title, card.dataset.inspired, card.dataset.brand, card.dataset.family, card.dataset.tags].join(' ')
      );
      card.dataset.lbbIndexed = '1';
      this.#refreshCard(card, this.#count());
    }
  }

  #applyFilters() {
    const q = this.#normalize(this.#filters.q);
    const terms = q ? q.split(' ') : [];
    let visible = 0;

    for (const card of this.#allCards()) {
      let show = true;

      if (terms.length) {
        const hay = card.dataset.search || '';
        show = terms.every((t) => hay.includes(t));
      }
      if (show && this.#filters.gender !== 'all') {
        show = (card.dataset.genders || '').includes(this.#filters.gender);
      }
      if (show && this.#filters.scents.size) {
        const fam = this.#normalize(card.dataset.family);
        show = Array.from(this.#filters.scents).some((s) => fam.includes(s));
      }

      card.toggleAttribute('data-filtered-out', !show);
      if (show) visible += 1;
    }

    this.toggleAttribute('data-empty', visible === 0 && this.#allLoaded);
    const results = this.#q('[data-lbb-results]');
    if (results) results.textContent = `${visible} fragrance${visible === 1 ? '' : 's'}${this.#allLoaded ? '' : '+'}`;
    this.#applySort();
  }

  #applySort() {
    const grid = this.#q('[data-lbb-grid]');
    if (!grid) return;
    const cmp = {
      featured: (a, b) => Number(a.dataset.featured) - Number(b.dataset.featured) || Number(a.dataset.pos) - Number(b.dataset.pos),
      az: (a, b) => a.dataset.title.localeCompare(b.dataset.title),
      za: (a, b) => b.dataset.title.localeCompare(a.dataset.title),
      new: (a, b) => Number(b.dataset.created) - Number(a.dataset.created),
    }[this.#filters.sort] || (() => 0);

    // Unbuyable fragrances stay in the catalogue but sink below the ones a
    // customer can actually add. Toggleable in the theme editor.
    const soldLast = this.dataset.soldOutLast === 'true'
      ? (a, b) => (this.#isSoldOut(a) ? 1 : 0) - (this.#isSoldOut(b) ? 1 : 0)
      : () => 0;

    this.#allCards().sort((a, b) => soldLast(a, b) || cmp(a, b)).forEach((c) => grid.appendChild(c));
  }

  /* ================= infinite scroll ================= */

  #setupInfiniteScroll() {
    const sentinel = this.#q('[data-lbb-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) this.#loadNextPage(); },
      { rootMargin: '700px 0px' }
    );
    this.#observer.observe(sentinel);
  }

  async #loadNextPage() {
    if (this.#loading || this.#allLoaded) return;
    this.#loading = true;
    this.setAttribute('data-loading', '');
    const status = this.#q('[data-lbb-load-status]');
    if (status) status.textContent = 'Loading more fragrances…';

    const next = this.#page + 1;
    const url = `${this.dataset.collectionUrl}?page=${next}&section_id=${this.dataset.sectionId}`;

    try {
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = Array.from(doc.querySelectorAll('[data-lbb-card]'));
      const grid = this.#q('[data-lbb-grid]');
      const seen = new Set(this.#allCards().map((c) => c.dataset.productId));
      let appended = 0;
      for (const card of incoming) {
        if (seen.has(card.dataset.productId)) continue;
        grid.appendChild(document.importNode(card, true));
        appended += 1;
      }
      this.#page = next;
      this.#allLoaded = next >= this.#totalPages || incoming.length === 0;
      this.#indexCards(this.#allCards());
      this.#buildScentChips();
      this.#applyFilters();
      this.#hydrateHoverImages();
      if (status) status.textContent = appended ? `${appended} more fragrances loaded.` : 'All fragrances loaded.';
    } catch (err) {
      console.warn('[bundle-builder] load failed', err);
      this.setAttribute('data-load-failed', '');
      if (status) status.textContent = 'Loading failed. Use the Load more button to retry.';
    } finally {
      this.#loading = false;
      this.removeAttribute('data-loading');
    }
  }

  async #ensureAllLoaded() {
    let guard = 0;
    while (!this.#allLoaded && guard < 20) {
      guard += 1;
      await this.#loadNextPage();
      if (this.hasAttribute('data-load-failed')) break;
    }
  }

  /* ================= milestone messaging ================= */

  #milestoneMessage(n) {
    const t = this.#tiers;
    if (!t.length) return '';
    const hl = (v) => `<span class="lbb-accent">${v}</span>`;
    const next = this.#nextTier(n);
    const cur = this.#currentTier(n);

    if (n === 0) {
      return `Add <strong>${t[0].qty} bottles</strong> to unlock ${hl(this.#reward(t[0]))}.`;
    }
    if (!next) {
      return `<strong>${this.#reward(cur)} unlocked.</strong> You’re on our best value.`;
    }
    if (!cur) {
      return `Add <strong>${this.#bottles(next.qty - n)}</strong> to unlock ${hl(this.#reward(next))}.`;
    }
    return `<strong>${this.#reward(cur)} unlocked.</strong> Add <strong>${this.#bottles(next.qty - n)}</strong> for ${hl(this.#reward(next))}.`;
  }

  /** Short plain-text version for the tray and summary panel. */
  #milestoneShort(n) {
    const next = this.#nextTier(n);
    if (n === 0) return this.#config.text.atcEmpty || '';
    if (!next) return 'Best value unlocked';
    return `Add ${this.#bottles(next.qty - n)} for ${this.#reward(next)}`;
  }

  /* ================= UI refresh ================= */

  #refreshCard(card, count) {
    const priceEl = card.querySelector('[data-lbb-price]');
    const wasEl = card.querySelector('[data-lbb-was]');
    const hintEl = card.querySelector('[data-lbb-hint]');
    const key = card.dataset.v50Id;
    const sel = key ? this.#selections.get(key) : null;
    const qty = sel?.qty || 0;
    const unit = Number(card.dataset.v50Price) || this.#unit;

    card.classList.toggle('is-selected', qty > 0);
    const badge = card.querySelector('[data-lbb-qty-badge]');
    if (badge) badge.textContent = String(qty);
    const out = card.querySelector('[data-lbb-qty-out]');
    if (out) out.textContent = String(qty);

    if (!priceEl) return;

    // Sold-out cards always show their shelf price and no bundle maths.
    if (this.#isSoldOut(card)) {
      priceEl.textContent = this.#moneyShort(unit);
      if (wasEl) wasEl.hidden = true;
      if (hintEl) hintEl.hidden = true;
      return;
    }

    const pct = this.#pctFor(count);
    if (pct > 0 && count > 0) {
      // Effective current per-bottle rate — truthful average of the live bundle.
      const eff = Math.round(this.#bundleTotal() / count);
      priceEl.textContent = this.#moneyShort(eff);
      if (wasEl) { wasEl.hidden = false; wasEl.textContent = this.#moneyShort(unit); }
      if (hintEl) {
        hintEl.hidden = false;
        hintEl.textContent = this.#config.text.inBundle || 'avg per bottle in your bundle';
      }
    } else {
      priceEl.textContent = this.#moneyShort(unit);
      if (wasEl) wasEl.hidden = true;
      if (hintEl) {
        const best = this.#tiers.reduce((m, t) => Math.max(m, t.pct), 0);
        if (best > 0) {
          const bestRate = Math.round(unit * (1 - best / 100));
          hintEl.hidden = false;
          hintEl.textContent = (this.#config.text.fromHint || 'From [rate]/bottle in bundles').replace('[rate]', this.#money(bestRate));
        } else {
          hintEl.hidden = true;
        }
      }
    }

    // Inventory ceiling on the + stepper
    const inc = card.querySelector('[data-lbb-inc]');
    if (inc) inc.disabled = qty > 0 && qty >= this.#capFor(card);
  }

  #refreshUI() {
    const n = this.#count();
    this.toggleAttribute('data-has-selection', n > 0);

    const retail = this.#subtotal();
    const pct = this.#pctFor(n);
    const saving = this.#savingCents();
    const bundle = retail - saving;
    const cur = this.#currentTier(n);
    const next = this.#nextTier(n);

    // Milestone crossing analytics
    this.#tiers.forEach((t, i) => {
      if (n >= t.qty && !this.#reachedTiers.has(i)) {
        this.#reachedTiers.add(i);
        this.#track('lbb_tier_reached', { tier: i, qty: t.qty, pct: t.pct });
        if (i > 0) this.#track('lbb_upsell_accepted', { toTier: i });
      }
      if (n < t.qty) this.#reachedTiers.delete(i);
    });

    // Slot selector + heading
    this.#renderSlots(n);
    const msgEl = this.#q('[data-lbb-progress-msg]');
    if (msgEl) msgEl.innerHTML = this.#milestoneMessage(n);
    const countEl = this.#q('[data-lbb-progress-count]');
    if (countEl) countEl.textContent = `${n} bottle${n === 1 ? '' : 's'}`;

    // Upsell impression tracking
    if (this.dataset.showUpsells === 'true' && next && n > 0) {
      const upsellKey = `${n}->${next.qty}`;
      if (this.#lastUpsellKey !== upsellKey) {
        this.#lastUpsellKey = upsellKey;
        this.#track('lbb_upsell_shown', { from: n, toQty: next.qty, toPct: next.pct });
      }
    }

    // Sticky bottom bar
    const nowEl = this.#q('[data-lbb-sticky-now]');
    const wasEl = this.#q('[data-lbb-sticky-was]');
    const saveEl = this.#q('[data-lbb-sticky-save]');
    const statusEl = this.#q('[data-lbb-sticky-status]');
    const stickyCount = this.#q('[data-lbb-sticky-count]');
    if (nowEl) nowEl.textContent = n ? `${n} bottle${n === 1 ? '' : 's'} · ${this.#money(bundle)}` : '';
    if (wasEl) wasEl.textContent = saving > 0 ? this.#money(retail) : '';
    if (saveEl) {
      const reward = this.#reward(cur);
      saveEl.textContent = saving > 0
        ? `${pct}% off · Save ${this.#moneyShort(saving)}`
        : (reward ? `${reward} unlocked` : '');
    }
    if (statusEl) statusEl.textContent = this.#milestoneShort(n);
    if (stickyCount) stickyCount.textContent = `(${n})`;
    this.#updateStickyBar();

    // Buttons
    const ready = n > 0 && !this.#submitting;
    this.#qa('[data-lbb-atc]').forEach((b) => {
      b.disabled = !ready;
      b.textContent = n > 0 ? (this.#config.text.atc || 'Add bundle to cart') : (this.#config.text.atcEmpty || 'Pick your perfumes');
    });

    // Price summary — the same hooks exist in the panel, the tray and the
    // drawer, so every match is updated rather than only the first.
    this.#qa('[data-lbb-total-was]').forEach((el) => { el.textContent = n ? this.#money(retail) : '—'; });
    this.#qa('[data-lbb-total-pct]').forEach((el) => { el.textContent = pct > 0 ? `${pct}% OFF` : '—'; });
    this.#qa('[data-lbb-total-save]').forEach((el) => { el.textContent = saving > 0 ? `−${this.#money(saving)}` : '—'; });
    this.#qa('[data-lbb-total-now]').forEach((el) => { el.textContent = n ? this.#money(bundle) : '—'; });

    const showUp = this.dataset.showUpsells === 'true' && next && n > 0;
    this.#qa('[data-lbb-summary-next], [data-lbb-drawer-upsell]').forEach((el) => {
      el.hidden = !showUp;
      if (showUp) el.textContent = this.#milestoneShort(n);
    });

    this.#renderDrawer();
    this.#allCards().forEach((c) => this.#refreshCard(c, n));
  }

  /* ================= summary drawer ================= */

  #openDrawer() {
    this.#renderDrawer();
    this.#q('[data-lbb-drawer]')?.showModal();
  }

  #renderDrawer() {
    const list = this.#q('[data-lbb-drawer-list]');
    if (!list) return;
    if (this.#selections.size === 0) {
      list.innerHTML = '<p class="lbb-drawer__empty">Nothing selected yet.</p>';
      return;
    }
    const rows = [];
    for (const [key, sel] of this.#selections) {
      const lineCents = sel.qty * (sel.priceCents || this.#unit);
      rows.push(`
        <div class="lbb-line" data-lbb-line="${this.#esc(key)}">
          <img src="${this.#esc(sel.image)}" alt="" loading="lazy" width="46" height="58">
          <div class="lbb-line__meta">
            <div class="lbb-line__title">${this.#esc(sel.title)}</div>
            <div class="lbb-line__sub">50ml${sel.inspired ? ` · Inspired by ${this.#esc(sel.inspired)}` : ''}</div>
            <div class="lbb-line__price">${this.#money(lineCents)}</div>
          </div>
          <div class="lbb-line__qty">
            <button type="button" data-lbb-line-dec aria-label="Remove one ${this.#esc(sel.title)}">−</button>
            <output aria-live="polite">${sel.qty}</output>
            <button type="button" data-lbb-line-inc aria-label="Add one more ${this.#esc(sel.title)}"${sel.cap != null && sel.qty >= sel.cap ? ' disabled' : ''}>+</button>
          </div>
        </div>
      `);
    }
    list.innerHTML = rows.join('');
  }

  /* ================= add to cart ================= */

  /**
   * v6: reverted to the v4 behaviour — add the bundle, confirm it landed,
   * hand off to the theme's cart drawer and stay on the page. No milestone
   * gate and no redirect to /checkout; the direct-checkout experiment in v5
   * is gone. Automatic discounts apply in the cart and at checkout as they
   * always did.
   *
   * The #submitting latch is set before any await and released in `finally`,
   * so a double-click cannot produce a duplicate add.
   */
  async #addBundleToCart() {
    const n = this.#count();
    if (this.#submitting || n === 0) return;

    this.#submitting = true;
    const buttons = this.#qa('[data-lbb-atc]');
    buttons.forEach((b) => { b.disabled = true; b.setAttribute('data-busy', ''); });

    const bundleCents = this.#bundleTotal();
    const pct = this.#pctFor(n);
    const cur = this.#currentTier(n);
    const tierLabel = cur
      ? `${cur.name || this.#reward(cur)} — ${n} bottles, ${this.#reward(cur)}`
      : `${n} bottle${n === 1 ? '' : 's'} — ${this.#moneyShort(bundleCents)}`;
    const bundleId = `LBB-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`.toUpperCase();

    // Line-item properties are unchanged from v3/v4 so any downstream Flow,
    // discount function or report keeps working. _bundle_pct is additive.
    const items = [];
    for (const [variantId, sel] of this.#selections) {
      items.push({
        id: Number(variantId),
        quantity: sel.qty,
        properties: {
          _bundle_id: bundleId,
          _bundle_name: 'Build Your Full-Size Rotation',
          _bundle_tier: tierLabel,
          _bundle_qty: String(n),
          _bundle_pct: String(pct),
          _bundle_price: this.#money(bundleCents),
          _bundle_discount: 'automatic',
        },
      });
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'bundle-builder',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    this.#track('lbb_atc', { bundleId, items: items.length, bottles: n, pct, valueCents: bundleCents });

    try {
      const cartAddUrl = (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        const message = data.description || data.message || this.#config.text.cartError;
        this.#highlightUnavailable(message);
        this.#toast(`${this.#config.text.soldOutError || 'Problem adding your bundle:'} ${message}`, true);
        this.#track('lbb_atc_error', { bundleId, message });
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'bundle-builder', sourceId: bundleId, itemCount: 0 },
        });
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'bundle-builder',
          sourceId: bundleId,
          itemCount: n,
          sections: data.sections,
        },
      });

      this.#toast('Bundle added to your cart.');
      this.#selections.clear();
      this.#reachedTiers.clear();
    } catch (err) {
      console.error('[bundle-builder] cart error', err);
      try { deferred.reject(err); } catch (e) { /* noop */ }
      this.#toast(this.#config.text.cartError || 'We couldn\u2019t add your bundle. Please try again.', true);
      this.#track('lbb_atc_error', { bundleId, message: String(err) });
    } finally {
      this.#submitting = false;
      buttons.forEach((b) => b.removeAttribute('data-busy'));
      this.#q('[data-lbb-drawer]')?.close();
      this.#refreshUI();
    }
  }

  #highlightUnavailable(message) {
    const msg = this.#normalize(message);
    if (!msg) return;
    for (const card of this.#allCards()) {
      const title = this.#normalize(card.dataset.title);
      if (title && msg.includes(title)) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const sold = card.querySelector('[data-lbb-soldout]');
        if (sold) { sold.hidden = false; sold.textContent = 'Just sold out — please swap'; }
        break;
      }
    }
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
